import React, { useEffect, useState, useCallback } from "react";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,

  getCountFromServer,

} from 'firebase/firestore';
import { db } from "../firebaseConfig";
import { auth } from "../firebaseConfig";
import { useAuth } from '../AuthContext';
import { useAppSelector, useAppDispatch } from '../redux/store';
import getCurrentScreen from "../Helper/GetScreen";

interface LeadStatus {
  id: string;
  status: string;
  color: string;
  priority: 'high' | 'medium' | 'low';
}

interface LeadData {
  statusId: string;
  [key: string]: any;
}

interface StatusCounts {
  [key: string]: number;
}

interface PriorityOrder {
  [key: string]: number;
  high: number;
  medium: number;
  low: number;
}

const LeadByStatus: React.FC = () => {
  const { userData, currentUser, isAdmin, isManager, isStaff } = useAuth();
  const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
  const [statusData, setStatusData] = useState<LeadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({});
  const [companyId, setCompanyId] = useState<string | null>(null);

  const fetchCompanyId = useCallback(async () => {
    const user = auth.currentUser;
    if (user) {
      const userDocRef = doc(db, "users", user.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        const userData = userDocSnap.data();
        setCompanyId(userData.companyId);
      } else {
        console.error("User document not found");
      }
    }
  }, []);

  useEffect(() => {
    fetchCompanyId();
  }, [fetchCompanyId]);

  useEffect(() => {
    const fetchStatusesAndLeads = async () => {
      if (!companyId) return;

      try {
        const statusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`));
        const fetchedStatuses = statusesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as LeadStatus[];
        const priorityOrder: PriorityOrder = { high: 1, medium: 2, low: 3 };
        const sortedStatuses = [...fetchedStatuses].sort(
          (a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]
        );

        setStatusData(sortedStatuses);
        const leadsRef = collection(db, `companies/${companyId}/leads`);
        const countPromises = sortedStatuses.map(async (status) => {
          let statusQuery = query(leadsRef, where('statusId', '==', status.id));
          
          if (isAdmin && currentScreen === 'Admin') {
            statusQuery = query(leadsRef, where('statusId', '==', status.id));
          } else if (isManager && currentScreen === 'Manager') {
            const usersQuery = query(
              collection(db, 'users'),
              where('companyId', '==', userData?.companyId),
              where('managerId', '==', currentUser?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);
            statusQuery = query(leadsRef, where('statusId', '==', status.id), where('assignedTo', 'in', users));
          } else if (isStaff && currentScreen === 'Staff') {
            statusQuery = query(leadsRef, where('statusId', '==', status.id), where('assignedTo', '==', currentUser?.uid));
          }

          const countSnapshot = await getCountFromServer(statusQuery);
          return {
            statusId: status.id,
            count: countSnapshot.data().count
          };
        });

        const statusCountResults = await Promise.all(countPromises);
        const counts: StatusCounts = {};
        statusCountResults.forEach(({ statusId, count }) => {
          counts[statusId] = count;
        });

        setStatusCounts(counts);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatusesAndLeads();
  }, [companyId]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <div
          className="flex items-center justify-center w-16 h-16 rounded-full"
          style={{
            background: "linear-gradient(180deg, #FFA782 0%, #FC583D 100%)",
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/images/desktop/priority-high.svg`}
            alt="Icon"
            className="w-10 h-10"
          />
        </div>
        <h2
          className="text-2xl text-black font-medium"
          style={{
            fontFamily: "Montserrat",
            lineHeight: "29.26px",
          }}
        >
          Lead By Status
        </h2>
      </div>

      <div className="grid grid-cols-1 gap-4 mb-6">
        {statusData.map((status) => (
          <div
            key={status.id}
            className="flex border-2 items-center bg-zinc-100 relative p-4 rounded-lg"
            style={{
              borderRadius: "32px 32px 32px 0",
              borderColor: status.color || "#652636",
            }}
          >
            <div className="flex items-center justify-between w-full px-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">{status.status}</span>
              </div>
              <div className="flex gap-3 items-center">
                <span className="text-lg text-gray-800 ">{statusCounts[status.id] || 0}</span>
                <div
                  className="flex items-center justify-center w-10 h-10 bg-white rounded-full "
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/images/desktop/iconamoon_arrow-top.svg`}
                    alt="Status"
                    className="w-6 h-6"
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

    </div>
  );
};

export default LeadByStatus;
